import {
  faChevronUp,
  faCode,
  faKeyboard,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Alert, Collapse, Dropdown, FormControl } from 'react-bootstrap';
import PlaceholderService from 'services/Placeholder.service';

const PlaceholderSelector = forwardRef(
  ({ onInsertPlaceholder, selectedTypes = [] }, ref) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [placeholders, setPlaceholders] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const dropdownMenuRef = useRef(null);

    const hasTicketDescription = selectedTypes.includes('TICKET_DESCRIPTION');

    useImperativeHandle(ref, () => ({
      open: () => {
        setShowDropdown(true);
        if (Object.keys(placeholders).length === 0) {
          getPlaceholders();
        }
      },
      close: () => {
        setShowDropdown(false);
      },
      isOpen: () => showDropdown
    }));

    useEffect(() => {
      if (showDropdown && Object.keys(placeholders).length === 0) {
        getPlaceholders();
      }
    }, [showDropdown]);

    useEffect(() => {
      if (showDropdown && Object.keys(placeholders).length > 0) {
        // Expande o primeiro grupo por padrão
        const firstGroup = Object.keys(placeholders)[0];
        setExpandedGroups({ [firstGroup]: true });
      }
    }, [placeholders, showDropdown]);

    const getPlaceholders = async () => {
      setIsLoading(true);
      try {
        const response = await PlaceholderService.list();
        setPlaceholders(response);
      } catch (error) {
        console.error('Erro ao carregar placeholders:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const handlePlaceholderClick = placeholderKey => {
      const placeholder = `{{${placeholderKey}}}`;
      if (onInsertPlaceholder) {
        onInsertPlaceholder(placeholder);
      }
      // Não fecha o dropdown para permitir inserir múltiplas variáveis
    };

    const toggleGroup = groupName => {
      setExpandedGroups(prev => ({
        ...prev,
        [groupName]: !prev[groupName]
      }));
    };

    const formatGroupName = groupName => {
      return groupName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const filterPlaceholders = () => {
      if (!searchTerm) return placeholders;

      const filtered = {};
      Object.entries(placeholders).forEach(([groupName, items]) => {
        const filteredItems = Object.entries(items).filter(
          ([key, label]) =>
            key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            label.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filteredItems.length > 0) {
          filtered[groupName] = Object.fromEntries(filteredItems);
        }
      });

      return filtered;
    };

    const filteredPlaceholders = filterPlaceholders();

    return (
      <>
        <style>
          {`
          .placeholder-toggle-btn::after {
            display: none !important;
          }
        `}
        </style>
        <Dropdown
          show={showDropdown}
          onToggle={isOpen => {
            setShowDropdown(isOpen);
          }}
          autoClose="outside"
        >
          <Dropdown.Toggle
            variant="outline-success"
            size="sm"
            className="rounded-circle p-0 d-flex align-items-center justify-content-center placeholder-toggle-btn"
            style={{
              width: '28px',
              height: '28px',
              border: '1px solid'
            }}
            title="Ver variáveis disponíveis para usar no conteúdo"
          >
            <FontAwesomeIcon icon={faCode} size="sm" />
          </Dropdown.Toggle>

          <Dropdown.Menu
            onClick={e => {
              // Previne que o dropdown feche ao clicar dentro dele
              e.stopPropagation();
            }}
            style={{
              maxWidth: '380px',
              width: '380px',
              maxHeight: '350px',
              overflowY: 'auto',
              padding: '0'
            }}
          >
            <div className="p-3">
              <Alert variant="info" className="mb-3 py-2">
                <div className="small">
                  <div className="mb-2">
                    <FontAwesomeIcon icon={faKeyboard} className="me-2" />
                    <strong>Atalho:</strong> Pressione{' '}
                    <kbd className="fs-11">Ctrl+Space</kbd> no editor para abrir
                    este menu.
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faCode} className="me-2" />
                    <strong>Autocomplete:</strong> Digite <code>{'{{'}</code> no
                    editor para ver sugestões automáticas.
                  </div>
                </div>
              </Alert>

              <div className="mb-3 position-relative">
                <FormControl
                  type="text"
                  placeholder="Procurar um espaço reservado"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="ps-4"
                  size="sm"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-muted"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>

              {isLoading ? (
                <div className="text-center text-muted py-4">
                  Carregando variáveis...
                </div>
              ) : Object.keys(filteredPlaceholders).length === 0 ? (
                <div className="text-center text-muted py-4">
                  Nenhuma variável encontrada
                </div>
              ) : (
                <div className="d-flex flex-column gap-1">
                  {Object.entries(filteredPlaceholders).map(
                    ([groupName, items]) => {
                      const isExpanded = expandedGroups[groupName];

                      return (
                        <div
                          key={groupName}
                          className="border-bottom pb-2 mb-2"
                        >
                          <button
                            type="button"
                            className="w-100 d-flex justify-content-between align-items-center p-2 border-0 bg-transparent text-start"
                            onClick={e => {
                              e.stopPropagation();
                              toggleGroup(groupName);
                            }}
                            style={{
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor =
                                'transparent';
                            }}
                          >
                            <span className="fw-medium">
                              {formatGroupName(groupName)}
                            </span>
                            <FontAwesomeIcon
                              icon={faChevronUp}
                              className="text-muted"
                              style={{
                                transform: isExpanded
                                  ? 'rotate(0deg)'
                                  : 'rotate(180deg)',
                                transition: 'transform 0.2s'
                              }}
                            />
                          </button>

                          <Collapse in={isExpanded}>
                            <div>
                              <div className="d-flex flex-column gap-1 ps-3">
                                {Object.entries(items).map(([key, label]) => (
                                  <button
                                    key={key}
                                    type="button"
                                    className="text-start border-0 bg-transparent p-2 rounded"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handlePlaceholderClick(key);
                                    }}
                                    title={`Clique para inserir: {{${key}}}`}
                                    style={{
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor =
                                        '#e9ecef';
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.backgroundColor =
                                        'transparent';
                                    }}
                                  >
                                    <div className="small fw-medium">
                                      {label}
                                    </div>
                                    <code
                                      className="text-muted"
                                      style={{ fontSize: '0.75rem' }}
                                    >
                                      {`{{${key}}}`}
                                    </code>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </Collapse>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </>
    );
  }
);

PlaceholderSelector.displayName = 'PlaceholderSelector';

export default PlaceholderSelector;
